import Moment from 'moment'
import ApiClient from '@api'
import dispatchActionForAllModules from '@utils/dispatch-action-for-all-modules'
import {
  updateApplicationLocale,
  getCurrentI18nLanguage,
  setLanguageAttribute,
} from '@src/i18n'
import { LANGUAGE_MAP } from '@utils/language-map'
import { loginApi, logoutApi, getUserApi, getClientIdApi } from './api'
import { getRoleNamesApi } from '@modules/roles/roles-api'
import { getEncryptedPassword, getDecryptedPassword } from '@utils/password'
import { generateFingerprint } from '@utils/fingerprint'

export const state = (function () {
  const initial = getAuthBlob() || {}
  return {
    user: initial.user || getSavedState('auth.user'),
    token: initial.token || getSavedState('auth.token'),
    loginType:
      initial.loginType || getSavedState('auth.loginType') || 'support-portal',
    twoFactorAuthToken:
      initial.twoFactorAuthToken || getSavedState('auth.twoFactorAuthToken'),
    isRtl: false,
    publicToken: initial.publicToken || getSavedState('publicToken'),
  }
})()

export const mutations = {
  SET_AUTHENTICATED_USER(state, newValue) {
    state.user = newValue
    persistAuthBlob({ user: newValue })
  },
  SET_CLIENT_ID(state, newValue) {
    state.publicToken = newValue
    persistAuthBlob({ publicToken: newValue })
    setDefaultPublicAuthHeaders(state)
  },
  SET_TOKEN(state, newValue) {
    state.token = newValue
    persistAuthBlob({ token: newValue })
    setDefaultAuthHeaders(state)
  },
  SET_LOGIN_TYPE(state, newValue) {
    state.loginType = newValue
    persistAuthBlob({ loginType: newValue })
  },
  SET_TWO_FACTORE_AUTH_TOKEN(state, newValue) {
    state.twoFactorAuthToken = newValue
    persistAuthBlob({ twoFactorAuthToken: newValue })
  },
  SET_SAML_REQUEST(state, newValue) {
    state.samlRequest = newValue
    saveState('auth.samlRequest', newValue)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  isPortalLogin(state) {
    return state.loginType === 'support-portal'
  },
  loginType(state) {
    return state.loginType
  },
  loggedIn(state) {
    return !!state.user
  },
  accessToken(state) {
    return state.token && state.token.access_token
  },
  publicAccessToken(state) {
    return state.publicToken && state.publicToken.access_token
  },
  samlRequest(state) {
    return state.samlRequest
  },
  twoFactorAuthToken(state) {
    return state.twoFactorAuthToken && state.twoFactorAuthToken
  },
  isCxoUser(state) {
    return !!(state.user && state.user.cxoUser)
  },
  isRtl(state) {
    if (state.user) {
      const lang = LANGUAGE_MAP[state.user.userLanguage]
      return ['ar'].indexOf(lang) >= 0
    }
    return false
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  async init({ state, dispatch }) {
    // Ensure auth header is set immediately to avoid early 401s on boot
    setDefaultAuthHeaders(state)
    try {
      migrateLegacyAuthIntoBlob()
      // Migrate fingerprint from blob to separate encrypted key if present
      try {
        const blobForFp = readAuthBlob() || {}
        if (blobForFp && blobForFp.fingerprint) {
          setEncryptedFingerprint(blobForFp.fingerprint)
          const { fingerprint, ...rest } = blobForFp
          writeAuthBlob(rest)
        }
      } catch (e) {}

      const currentFingerprint = await generateFingerprint()
      const storedFingerprint = getEncryptedFingerprint()
      if (storedFingerprint && storedFingerprint !== currentFingerprint) {
        await dispatch('logout')
        setEncryptedFingerprint(currentFingerprint)
      } else if (!storedFingerprint) {
        setEncryptedFingerprint(currentFingerprint)
      }
    } catch (e) {
      await dispatch('logout')
    }

    dispatch('validate')
  },

  // Logs in the current user.
  login({ commit, dispatch, getters }, { username, password, loginType } = {}) {
    if (getters.loggedIn) return dispatch('validate')
    return loginApi(username, password)
      .then((token) => {
        commit('SET_TOKEN', token)
        commit('SET_LOGIN_TYPE', loginType)
        try {
          const fp = generateFingerprint()
          setEncryptedFingerprint(fp)
        } catch (e) {}
        if (token.uarToken) {
          return {
            resetToken: token.uarToken,
          }
        } else {
          return getUserApi()
        }
      })
      .then((user) => {
        if (user.id) {
          commit('SET_AUTHENTICATED_USER', user)
        }
        return user
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          commit('SET_AUTHENTICATED_USER', null)
          commit('SET_TOKEN', null)
        }
        return null
      })
  },

  fingerprint({ commit }, { fingerprint } = {}) {
    return getClientIdApi(fingerprint).then((response) => {
      const clientId = getDecryptedPassword(response.clientId)
      const [username, password] = atob(clientId).split(':')

      return loginApi(
        username,
        password,
        {
          grant_type: 'client_credentials',
        },
        {
          Authorization: `Basic ${clientId}`,
        }
      ).then((token) => {
        commit('SET_CLIENT_ID', token)
      })
    })
  },

  changeLoginType({ commit }, type) {
    commit('SET_LOGIN_TYPE', type)
  },

  setToken({ commit }, token) {
    commit('SET_TOKEN', token)
  },

  setSamlRequest({ commit }, samlRequest) {
    commit('SET_SAML_REQUEST', samlRequest)
  },

  setTwoFcatorAuthToken({ commit }, token) {
    commit('SET_TWO_FACTORE_AUTH_TOKEN', token)
  },

  getUser({ commit }, params = { pageReload: false }) {
    return getUserApi()
      .then((user) => {
        commit('SET_AUTHENTICATED_USER', user)
        const lang = LANGUAGE_MAP[user.userLanguage]
        setLanguageAttribute(lang)
        if (getCurrentI18nLanguage() !== lang) {
          Moment.locale(lang)
          updateApplicationLocale(lang).then(() => {
            if (params.pageReload) {
              window.location.reload()
            }
          })
        }
        if (user.cxoUser === true) {
          return getRoleNamesApi(user.roles).then((roleNames) => {
            commit('SET_AUTHENTICATED_USER', { ...user, roleNames })
            return user
          })
        } else {
          return user
        }
      })
      .catch((e) => e)
  },

  // Logs out the current user.
  logout({ commit }) {
    console.warn('[LOGOUT_MONITOR] Dispatching auth/logout action (Vuex)')
    logoutApi()
    commit('SET_AUTHENTICATED_USER', null)
    commit('SET_TOKEN', null)
    commit('SET_LOGIN_TYPE', 'support-portal')
    try {
      removeAuthBlob()
      removeEncryptedFingerprint()
    } catch (e) {}
    dispatchActionForAllModules('destroy', { exclude: ['auth', 'ssoConfig'] })
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state, dispatch }) {
    if (!state.user) {
      if (state.publicToken && state.publicToken.access_token) {
        setDefaultPublicAuthHeaders(state)
      }
      return Promise.resolve(null)
    }
    if (state.user && state.token && state.token.expires_at > Moment().unix()) {
      return Promise.resolve(state.user)
    }
    dispatch('logout')
    return Promise.resolve(null)
    // @TODO replace this with refresh token mechanism
    // return new Promise((resolve) => {
    //   return getUserApi()
    //     .then((user) => {
    //       commit('SET_AUTHENTICATED_USER', user)
    //       resolve(user)
    //     })
    //     .catch(() => {
    //       // @TODO handle refresh token once
    //       dispatch('logout')
    //       resolve(null)
    //     })
    // })
  },
}

// ===
// Private helpers
// ===

export function getSavedState(key) {
  if (window.localStorage.getItem(key)) {
    try {
      return JSON.parse(getDecryptedPassword(window.localStorage.getItem(key)))
    } catch (error) {
      return null
    }
  }
  return ''
}

function saveState(key, state) {
  window.localStorage.setItem(key, getEncryptedPassword(JSON.stringify(state)))
}

// Consolidated auth blob helpers
export function getAuthBlobKey() {
  return getEncryptedPassword('auth.blob')
}

function readAuthBlob() {
  const encKey = getAuthBlobKey()
  const cipher = window.localStorage.getItem(encKey)
  if (!cipher) return null
  try {
    const json = getDecryptedPassword(cipher)
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

function writeAuthBlob(blob) {
  const encKey = getAuthBlobKey()
  const json = JSON.stringify(blob || {})
  window.localStorage.setItem(encKey, getEncryptedPassword(json))
}

function persistAuthBlob(partial) {
  const current = readAuthBlob() || {}
  const next = { ...current, ...partial }
  writeAuthBlob(next)
}

function removeAuthBlob() {
  const encKey = getAuthBlobKey()
  window.localStorage.removeItem(encKey)
}

function getAuthBlob() {
  return readAuthBlob()
}

function migrateLegacyAuthIntoBlob() {
  const existing = readAuthBlob() || {}
  const legacy = {
    user: getSavedState('auth.user') || existing.user,
    token: getSavedState('auth.token') || existing.token,
    loginType: getSavedState('auth.loginType') || existing.loginType,
    twoFactorAuthToken:
      getSavedState('auth.twoFactorAuthToken') || existing.twoFactorAuthToken,
    publicToken: getSavedState('publicToken') || existing.publicToken,
  }
  // If any legacy field exists and blob not yet set, write blob
  const shouldWrite =
    legacy.user ||
    legacy.token ||
    legacy.loginType ||
    legacy.twoFactorAuthToken ||
    legacy.publicToken
  if (shouldWrite) {
    writeAuthBlob({ ...existing, ...legacy })
    // Clean up legacy localStorage keys after successful migration
    removeLegacyAuthKeys()
  }
}

function removeLegacyAuthKeys() {
  try {
    const legacyKeys = [
      'auth.user',
      'auth.token',
      'auth.loginType',
      'auth.twoFactorAuthToken',
      'publicToken',
    ]
    legacyKeys.forEach((key) => {
      // const encKey = getEncryptedPassword(key)
      window.localStorage.removeItem(key)
    })
  } catch (e) {
    // Silently fail if cleanup errors occur
  }
}

// Fingerprint helpers (separate encrypted key)
function getEncryptedFingerprint() {
  try {
    const encKey = getEncryptedPassword('auth.fingerprint')
    const cipher = window.localStorage.getItem(encKey)
    if (!cipher) return ''
    return getDecryptedPassword(cipher)
  } catch (e) {
    return ''
  }
}

function setEncryptedFingerprint(value) {
  try {
    const encKey = getEncryptedPassword('auth.fingerprint')
    window.localStorage.setItem(encKey, getEncryptedPassword(value))
  } catch (e) {}
}

function removeEncryptedFingerprint() {
  try {
    const encKey = getEncryptedPassword('auth.fingerprint')
    window.localStorage.removeItem(encKey)
  } catch (e) {}
}

function setDefaultAuthHeaders(state) {
  if (state.token && state.token.access_token) {
    ApiClient.setHeaders({
      Authorization: `Bearer ${state.token.access_token}`,
    })
  } else {
    ApiClient.removeHeader('Authorization')
  }
}

function setDefaultPublicAuthHeaders(state) {
  if (state.publicToken && state.publicToken.access_token) {
    ApiClient.setHeaders({
      Authorization: `Bearer ${state.publicToken.access_token}`,
    })
  }
}
